import { format, isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

function formatMoney(amount) {
  if (Number(amount) === NaN) return '';

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(amount);
}

function formatTelephoneNumber(telephoneNumber) {
  const cleaned = (`${telephoneNumber}`).replace(/\D/g, '')
    .trim();
  let match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
  }
  match = cleaned.match(/^(\d{2})(\d{2})(\d{4})(\d{4})$/);
  if (match) {
    return `+${match[1]}-${match[2]}-${match[3]}-${match[4]}`;
  }

  // To format US/CA phone numbers
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  return telephoneNumber;
}

function formatToPrecision(number, precision = 2) {
  return parseFloat(number.toFixed(precision));
}

function formatISODate(isoDate, dateFormat = 'PPP') {
  if (isoDate === 'N/A') return 'N/A';
  try {
    return format(parseISO(isoDate), dateFormat);
  } catch (e) {
    return 'Error parsing isoDate string';
  }
}

/**
   * This function will format provider's hours of operations into a more compact view.
   * @param {string} hoursOfOperation
   * @returns string
   */
function formatHoursOfOperation(hoursOfOperation) {
  if (hoursOfOperation.includes(';')) {
    const dayTime = hoursOfOperation.split(';').map((weekday) => {
      const weekDayEntry = weekday.trim().split(/\s(?=\d)/);

      return { day: weekDayEntry[0], time: `${weekDayEntry[1]} ${weekDayEntry[2]}` };
    });

    const dayOrder = {
      Monday: 0,
      Tuesday: 1,
      Wednesday: 2,
      Thursday: 3,
      Friday: 4,
      Saturday: 5,
      Sunday: 6,
    };

    const daySorter = dayTime.sort((a, b) => (
      dayOrder[a.day] - dayOrder[b.day]
    ));

    const combinedHoursOfOperation = [];
    let currentHours = { days: [], time: '' };

    daySorter.forEach((entry, index) => {
      if (currentHours.days.length === 0) {
        currentHours = { days: [entry.day], time: entry.time };
      }

      if (entry.time === currentHours.time) {
        currentHours.days.push(entry.day);
      } else {
        if (currentHours.days.length > 0) {
          combinedHoursOfOperation.push(currentHours);
        }

        currentHours = { days: [entry.day], time: entry.time };
      }

      if (index === dayTime.length - 1) {
        combinedHoursOfOperation.push(currentHours);
      }
    });

    const finalHours = combinedHoursOfOperation.map(({ days, time }) => {
      if (days.length > 1) {
        return `${days[0]} - ${days[days.length - 1]} ${time}`;
      }
      return `${days[0]} ${time}`;
    }).join(', ');

    return finalHours;
  }

  return hoursOfOperation;
}

function camelCaseToNormalText(text) {
  return text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
}

function formatDate(date, dateFormat = 'MM/dd/yyyy') {
  if (!date) return '';
  let d = date;
  if (typeof date === 'string') {
    d = parseISO(date);
  }
  return isValid(d) ? format(d, dateFormat) : '';
}

function formatISOToLocalTime(isoString, dateFormat = 'MMM dd, yyyy h:mm a zzz', fallback = 'Error formatting date to local time') {
  if (!isoString) return fallback;

  try {
    const parsedDate = parseISO(isoString);
    if (!isValid(parsedDate)) return fallback;

    const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
    return formatInTimeZone(parsedDate, timeZone, dateFormat);
  } catch (error) {
    return fallback;
  }
}

export default {
  formatMoney,
  formatTelephoneNumber,
  formatToPrecision,
  formatISODate,
  formatHoursOfOperation,
  camelCaseToNormalText,
  formatDate,
  formatISOToLocalTime
};
